import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Tabs,
  Tag,
  Typography,
  Space,
  Popover,
  Flex,
  Spin,
  Avatar,
  Tooltip,
  Switch,
  Checkbox,
  Button,
} from "antd";
import moment from "moment";
import MobileViewComponent from "./MobileViewComponent";
import { useMediaQuery } from "react-responsive";
import { Link } from "react-router-dom";
import orderService from "../../services/OrderLists";
import "./tasks.css";
import { CloudHail, TableColumnsSplit,  } from "lucide-react";
import { InfoCircleOutlined, SettingOutlined } from "@ant-design/icons";
import usePermissions from "../../hooks/usePermissions";
import AssignTagsPopover from "../../components/OrderTags/AssignTagsPopover";

const { TabPane } = Tabs;
const { Text } = Typography;

const OrderListTable = ({
  orders,
  loading,
  pagination,
  onChange,
  setUrgencyFilter,
  selectedOption,
  loadOrders, // Correctly passing loadOrders here
  setPagination,
  toggleAssignedOrders,
  setToggleAssignedOrders,
  allTags = [],
  taggedTaskKeys = new Set(),
  onTagsChanged,
}) => {
  const [freelancerData, setFreelancerData] = useState(null);
  const [columns, setColumns] = useState([]);
  const [flLoading, setFlLoading] = useState(false);
  const [allColumns, setAllColumns] = useState([]);
  const [selectedColumnKeys, setSelectedColumnKeys] = useState([]);

  const allTagsRef = useRef(allTags);
  const taggedTaskKeysRef = useRef(taggedTaskKeys);
  const onTagsChangedRef = useRef(onTagsChanged);
  allTagsRef.current = allTags;
  taggedTaskKeysRef.current = taggedTaskKeys;
  onTagsChangedRef.current = onTagsChanged;

  const handleTabChange = (key) => setUrgencyFilter(key);
  const {hasPermission} = usePermissions();
  
  const handleToggleAssignedOrders= ()=>{
    setToggleAssignedOrders(!toggleAssignedOrders);
  }

  useEffect(() => {
    const renderSubscribers = (subscribers) => {
      if (!subscribers || subscribers.length === 0) return "N/A";

      const displayedSubscribers = subscribers.slice(0, 2);
      const remainingSubscribers = subscribers.slice(2);

      return (
        <Avatar.Group maxCount={3}>
          {displayedSubscribers.map((name, index) => (
            <Tooltip key={index} title={name}>
              <Avatar style={{ backgroundColor: "#1890ff" }}>
                {name.charAt(0).toUpperCase()}
              </Avatar>
            </Tooltip>
          ))}
          {remainingSubscribers.length > 0 && (
            <Popover
              content={
                <div>
                  {remainingSubscribers.map((name, index) => (
                    <p key={index}>{name}</p>
                  ))}
                </div>
              }
              title="Additional Subscribers">
              <Avatar style={{ backgroundColor: "#f56a00" }}>
                +{remainingSubscribers.length}
              </Avatar>
            </Popover>
          )}
        </Avatar.Group>
      );
    };

    if (orders.length > 0) {
      const dynamicColumns = Object.keys(orders[0])
        .map((key) => {
          const dynamicTitle = key
            .replace(/_/g, " ")
            .replace(/\b\w/g, (letter) => letter.toUpperCase()); // Dynamically creating title

          switch (key) {
            case "alloted_deadline":
              return {
                title: dynamicTitle,
                dataIndex: "alloted_deadline",
                key: "alloted_deadlinet",
                render: (text) => (
                  <Text>
                    {moment(text).local().format("DD-MM-YYYY hh:mm A")}
                  </Text>
                ),
              };
            case "key":
              return {
                title: dynamicTitle,
                dataIndex: "key",
                key: "key",
                fixed: "left",
                width: 150,
                render: (text, record) => (
                  <a
                    href={`/order-management/my-orders/${record.key}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none" }}
                  >
                    <Text style={{ color: "black", fontWeight: 600 }}>
                      {text}
                    </Text>
                  </a>
                ),
              };

            case "title":
              return {
                title: dynamicTitle,
                dataIndex: "title",
                key: "title",
                width: 200,
                render: (text) => (
                  <Text ellipsis={{ tooltip: text }} style={{ maxWidth: 150 }}>
                    {text}
                  </Text>
                ),
              };

            case "start_date":
              return {
                title: dynamicTitle,
                dataIndex: "start_date",
                key: "start_date",
                render: (text) => (
                  <Text>
                    {moment(text).local().format("DD-MM-YYYY hh:mm A")}
                  </Text>
                ),
              };
            case "client_deadline":
              return {
                title: dynamicTitle,
                dataIndex: "client_deadline",
                key: "client_deadline",
                render: (text) => (
                  <Text>
                    {moment(text).local().format("DD-MM-YYYY hh:mm A")}
                  </Text>
                ),
              };
            case "tags":
              return {
                title: "Tags",
                dataIndex: "tags",
                key: "tags",
                width: 150,
                render: (tags) =>
                  tags?.length > 0 ? (
                    <span>
                      {tags.map((tag) => (
                        <Tag color="purple" key={tag} style={{ marginBottom: 2 }}>
                          {tag}
                        </Tag>
                      ))}
                    </span>
                  ) : null,
              };
            case "payment":
              return [
                {
                  title: dynamicTitle,
                  dataIndex: "budget",
                  key: "budget",
                  width: 180,
                  render: (_, record) => {
                    const payment = record?.payment;

                    if (!payment) return "N/A";

                    const { budget, amount_paid, currency, payment_status } =
                      payment;

                    const statusDotColor = {
                      Full: "green",
                      Partial: "yellow",
                      Unpaid: "red",
                    };

                    return (
                      <Space direction="vertical" size={0}>
                        <Text strong>
                          {!budget ? "N/A" : `${currency} ${budget}`}
                        </Text>
                        <Space>
                          <span
                            style={{
                              display: "inline-block",
                              width: "8px",
                              height: "8px",
                              borderRadius: "50%",
                              backgroundColor:
                                statusDotColor[payment_status] || "gray",
                            }}></span>
                          <Text
                            type={
                              payment_status === "Full"
                                ? "success"
                                : payment_status === "Partial"
                                ? "warning"
                                : "danger"
                            }>
                            {payment_status === "Full"
                              ? "Paid"
                              : payment_status === "Partial"
                              ? "Partial"
                              : "Unpaid"}
                          </Text>
                        </Space>
                      </Space>
                    );
                  },
                },
                {
                  title: "Amount Paid",
                  dataIndex: "payment",
                  key: "amount_paid",
                  width: 180,
                  render: (_, record) => {
                    const { amount_paid, currency } = record?.payment || {};
                    return (
                      <Text>
                        {amount_paid ? `${currency} ${amount_paid}` : "N/A"}
                      </Text>
                    );
                  },
                },
              ];

            case "moved_to_fl":
              return {
                title: dynamicTitle,
                dataIndex: "moved_to_fl",
                key: "moved_to_fl",
                width: 150,
                render: (_, record) => {
                  const fl = record?.moved_to_fl ? "Yes" : "No";

                  const handlePopoverOpen = async () => {
                    try {
                      setFlLoading(true);
                      if (record?.moved_to_fl_team && !freelancerData) {
                        const response = await orderService.getFreelancerDetail(
                          record.key
                        );
                        if (response) {
                          setFreelancerData(response?.data?.data);
                        }
                      }
                    } catch (error) {
                      setFlLoading(false);
                      console.error(
                        "Error fetching freelancer details:",
                        error
                      );
                    } finally {
                      setFlLoading(false);
                    }
                  };

                  // Define columns for Ant Design Table
                  const columns = [
                    {
                      title: "Key",
                      dataIndex: "key",
                      key: "key",
                      width: 120,
                      render: (text) => <strong>{text}</strong>,
                    },
                    {
                      title: "Value",
                      dataIndex: "value",
                      key: "value",
                      render: (text) => <span>{text}</span>,
                    },
                  ];

                  // Transforming data to match table structure
                  const tableData = freelancerData
                    ? Object.entries(freelancerData).flatMap(
                        ([key, value], index) => {
                          if (Array.isArray(value)) {
                            // Handle nested arrays (like budget or subscribers)
                            if (
                              value.length > 0 &&
                              typeof value[0] === "object"
                            ) {
                              return value.flatMap((item, subIndex) =>
                                Object.entries(item).map(
                                  ([subKey, subValue], idx) => ({
                                    key: `${key}-${subIndex}-${idx}`,
                                    keyLabel: `${subKey}`,
                                    value: String(subValue),
                                  })
                                )
                              );
                            }
                            return [
                              {
                                key: index,
                                keyLabel: key,
                                value: value.join(", "),
                              },
                            ];
                          }
                          return [
                            {
                              key: index,
                              keyLabel: key,
                              value: String(value),
                            },
                          ];
                        }
                      )
                    : [];

                  // Popover content
                  const content = flLoading ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}>
                      <Spin size="large" />
                    </div>
                  ) : (
                    <Table
                      columns={columns}
                      dataSource={tableData}
                      pagination={false}
                      size="small"
                      bordered
                      style={{ width: 300 }}
                    />
                  );

                  return record?.moved_to_freelancer_portal ? (
                    <Popover
                      content={content}
                      title="Freelancer Details"
                      trigger="hover"
                      // onVisibleChange={handlePopoverOpen}
                    >
                      <Text>{fl}</Text>
                    </Popover>
                  ) : (
                    <Text>{fl}</Text>
                  );
                },
              };
            case "moved_to_fl_team":
              return {
                title: dynamicTitle,
                dataIndex: "moved_to_fl_team",
                key: "moved_to_fl_team",
                render: (text, record) => {
                  // Check the value and return "Yes" or "No"
                  const movedToTeam = record?.moved_to_fl_team ? "Yes" : "No";
                  return <Text>{movedToTeam}</Text>;
                },
              };
            case "is_executive":
              return {
                title: dynamicTitle,
                dataIndex: "is_executive",
                key: "is_executive",
                render: (text, record) => {
                  // Check the value and return "Yes" or "No"
                  const movedToTeam = record?.is_executive ? "Yes" : "No";
                  return <Text>{movedToTeam}</Text>;
                },
              };
            case "subscribers":
              return {
                title: dynamicTitle,
                dataIndex: "subscribers",
                key: "subscribers",
                width: 200,
                render: (subscribers) => renderSubscribers(subscribers),
              };
            case "status":
            case "cancellation_reason":
              return null;

            default:
              return {
                title: key
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (letter) => letter.toUpperCase()),
                dataIndex: key,
                key,
                render: (text) => <Text>{text || "N/A"}</Text>,
              };
          }
        })
        .flat()
        .filter((column) => column);
      // Move tags column to immediately after title
      const titleColumnIndex = dynamicColumns.findIndex(
        (column) => column.dataIndex === "title"
      );
      const tagsColumnIndex = dynamicColumns.findIndex(
        (column) => column.dataIndex === "tags"
      );
      if (titleColumnIndex !== -1 && tagsColumnIndex !== -1) {
        const [tagsColumn] = dynamicColumns.splice(tagsColumnIndex, 1);
        dynamicColumns.splice(titleColumnIndex + 1, 0, tagsColumn);
      }

      // Reorder columns: move 'alloted_deadline' to immediately after 'key'
      const keyColumnIndex = dynamicColumns.findIndex(
        (column) => column.dataIndex === "key"
      );
      const allotedDeadlineColumnIndex = dynamicColumns.findIndex(
        (column) => column.dataIndex === "alloted_deadline"
      );

      if (keyColumnIndex !== -1 && allotedDeadlineColumnIndex !== -1) {
        const [allotedDeadlineColumn] = dynamicColumns.splice(
          allotedDeadlineColumnIndex,
          1
        );
        dynamicColumns.splice(keyColumnIndex + 2, 0, allotedDeadlineColumn);
      }

      dynamicColumns.push({
        title: "Status",
        dataIndex: "status",
        key: "status",
        fixed: "right",
        width: 150,
        sorter: (a, b) => {
          if (a.status > b.status) return 1;
          if (a.status < b.status) return -1;
          return 0;
        },

        sortDirections: ["ascend", "descend"],
        render: (text) => {
          const statusColors = {
            Unassigned: "#F0F0F0", // Lighter Gray
            "Assigned to manager": "#C0C0C0", // Lighter Dark Gray
            "In Progress": "#D0E4F2", // Lighter Blue
            "Quality Check": "#A9C3E0", // Lighter Medium Blue
            Completed: "#C8E6C8", // Lighter Light Green
            Delivered: "#8FCF8F", // Lighter Medium Green
            Rejected: "#E8A0A0", // Lighter Soft Red
            Cancelled: "#A0A0A0", // Lighter Dark Gray
            "Cancelled by User": "#A0A0A0",
            Failed: "#D09090", // Lighter Darker Red
            Feedback: "#C2A892", // Lighter Muted Brown
          };
          return (
            <Tag
            style={{color:'black' , fontWeight:500}}
              color={statusColors[text] || "default"}>
              {text}
            </Tag>
          );
        },
      });

      dynamicColumns.push({
        title: "Cancellation Reason",
        dataIndex: "cancellation_reason",
        key: "cancellation_reason",
        width: 200,
        render: (text) => <Text>{text || "-"}</Text>,
      });

      // Tag assignment column
      dynamicColumns.push({
        title: "Tag",
        key: "tag_action",
        fixed: "right",
        width: 50,
        render: (_, record) => (
          <AssignTagsPopover
            taskKey={record.key}
            allTags={allTagsRef.current}
            isTagged={taggedTaskKeysRef.current.has(record.key)}
            onTagsChanged={onTagsChangedRef.current}
          />
        ),
      });

      // Set the updated columns
      const defaultHidden = new Set(["cancellation_reason"]);
      setAllColumns(dynamicColumns);
      setColumns((prev) => {
        // If user hasn't selected any, default to all except hidden-by-default columns
        if (!selectedColumnKeys || selectedColumnKeys.length === 0) {
          const defaultKeys = dynamicColumns
            .map((c) => c.key)
            .filter((k) => !defaultHidden.has(k));
          setSelectedColumnKeys(defaultKeys);
          return dynamicColumns.filter((c) => !defaultHidden.has(c.key));
        }
        // Else filter by selected keys, keeping order
        const selectedSet = new Set(selectedColumnKeys);
        return dynamicColumns.filter((c) => selectedSet.has(c.key));
      });
    }
  }, [orders]);

  useEffect(() => {
    if (allColumns.length > 0) {
      const setKeys = new Set(selectedColumnKeys);
      setColumns(allColumns.filter((c) => setKeys.has(c.key)));
    }
  }, [selectedColumnKeys, allColumns]);

  const getColLabel = (col) => {
    if (typeof col.title === "string") return col.title;
    if (typeof col.dataIndex === "string") return col.dataIndex;
    return col.key;
  };

  const columnSelector = (
    <Popover
      placement="bottomRight"
      trigger="click"
      title="Select Columns"
      content={
        <div style={{ maxWidth: 260 }}>
          <Checkbox.Group
            style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8 }}
            value={selectedColumnKeys}
            onChange={(vals) => setSelectedColumnKeys(vals)}
          >
            {allColumns.map((col) => (
              <Checkbox key={col.key} value={col.key}>
                {getColLabel(col)}
              </Checkbox>
            ))}
          </Checkbox.Group>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12 }}>
            <Button size="small" onClick={() => setSelectedColumnKeys(allColumns.map((c) => c.key))}>Select All</Button>
            <Button size="small" onClick={() => setSelectedColumnKeys([])}>Clear</Button>
          </div>
        </div>
      }
    >
      <Button icon={<SettingOutlined />} type="default"></Button>
    </Popover>
  );

  const tabConfig = [
    { key: "all", label: "All Orders", color: "blue", bgColor: "grey" },
    { key: "red", label: "Deadline Missed", color: "red", bgColor: "#FFCCCB" },
    {
      key: "orange",
      label: "Deadline > 1 Day",
      color: "orange",
      bgColor: "#FFE5B4",
    },
    {
      key: "yellow",
      label: "Deadline in 2-4 Days",
      color: "#FACA15",
      bgColor: "#FFF9C4",
    },
    {
      key: "green",
      label: "Deadline > 4 Days",
      color: "green",
      bgColor: "#C8E6C9",
    },
  ];

  return (
    <div style={{ width: "100%" }}>
      <Tabs
        onChange={handleTabChange}
        defaultActiveKey="all"
        tabBarExtraContent={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {hasPermission("can_view_superuser_orders") && selectedOption === "my-orders" ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  backgroundColor: '#e6f9fb',
                  padding: '5px 15px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                }}
              >
                <Tooltip
                  placement="left"
                  title={`Switch to view ${toggleAssignedOrders ? "Superuser" : "Assigned"} Orders`}
                  arrowPointAtCenter
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{ color: 'black', fontWeight: 500 }}>
                      {!toggleAssignedOrders ? "Superuser Orders" : "Assigned Orders"}
                    </span>
                  </div>
                </Tooltip>

                <Switch
                  value={toggleAssignedOrders}
                  checked={toggleAssignedOrders}
                  onChange={handleToggleAssignedOrders}
                  style={{
                    backgroundColor: toggleAssignedOrders ? '#36c6d3' : 'black',
                  }}
                />
              </div>
            ) : null}

            {columnSelector}
          </div>
        }
      >
        {tabConfig.map((tab) => (
          <TabPane
            tab={
              <span
                style={{
                  color: "black",
                  padding: "8px 16px",
                  borderRadius: "8px 8px 0 0",
                  backgroundColor: tab.bgColor,
                }}>
                {tab.label}
              </span>
            }
            key={tab.key}>
            <Flex vertical align="flex-end" gap={10} style={{ width: "100%" }}>
              {!loading && (
                <p style={{ margin: 0, padding: 0 }}>
                  {" "}
                  Showing{" "}
                  {Math.min(
                    (pagination.current - 1) * pagination.pageSize + 1,
                    pagination.total
                  )}{" "}
                  -
                  {Math.min(
                    pagination.current * pagination.pageSize,
                    pagination.total
                  )}{" "}
                  of {pagination.total} Orders.
                </p>
              )}

              <Table
                style={{ width: "100%" }}
                columns={columns}
                dataSource={orders}
                loading={loading}
                pagination={pagination}
                onChange={onChange}
                scroll={{ x: "max-content" }}
                rowClassName={(record, index) =>
                  index % 2 === 0 ? "even-row" : "odd-row"
                }
              />
            </Flex>
          </TabPane>
        ))}
      </Tabs>
    </div>
  );
};

export default OrderListTable;
